import type { AxiosError } from 'axios'
import type { ProfileApiResponse, UpdateTimezonePayload, UserProfile } from '@app/types/profile/interfaces'

import { useProfileStore } from '@stores/profile'
import { useCommonStore } from '@stores/common'

import { useAdminRoutes } from '@composables/routes/admin-route.hook'
import { useOptionsApi } from '@api/profile/options.hook'
import { usePrivilegesApi } from '@api/profile/privileges.hook'

interface UseProfileApiOptions<T = UserProfile> {
  onStart?: () => void
  onSuccess?: (data: T) => void
  onError?: (err: AxiosError) => void
}

interface UseProfileApiReturn {
  fetchProfile: (options?: UseProfileApiOptions) => Promise<UserProfile | null>
  updateViewTimezone: (payload: UpdateTimezonePayload, options?: UseProfileApiOptions) => Promise<UserProfile | null>
}

export const useProfileApi = (): UseProfileApiReturn => {
  const { $axios } = useNuxtApp()
  const profileStore = useProfileStore()
  const commonStore = useCommonStore()

  const fetchProfile = async (options?: UseProfileApiOptions): Promise<UserProfile | null> => {
    commonStore.setLoading(true)
    options?.onStart?.()

    const url = useAdminRoutes('admin.profile.show')
    if (!url) return null

    try {
      const { data } = await $axios.get<ProfileApiResponse>(url)
      profileStore.setUser(data.data)
      commonStore.setLoading(false)
      options?.onSuccess?.(data.data)
      await Promise.all([useOptionsApi(), usePrivilegesApi()])
      return data.data
    } catch (err) {
      commonStore.setLoading(false)
      options?.onError?.(err as AxiosError)
      return null
    }
  }

  const updateViewTimezone = async (
    payload: UpdateTimezonePayload,
    options?: UseProfileApiOptions,
  ): Promise<UserProfile | null> => {
    options?.onStart?.()

    const url = useAdminRoutes('admin.profile.view-timezone')
    if (!url) return null

    try {
      const { data } = await $axios.post<ProfileApiResponse>(url, payload)
      profileStore.setUser({ ...data.data, slot: data.data.slot ?? profileStore.getUser?.slot ?? null })
      options?.onSuccess?.(data.data)
      await useOptionsApi()
      return data.data
    } catch (err) {
      options?.onError?.(err as AxiosError)
      return null
    }
  }

  return {
    fetchProfile,
    updateViewTimezone,
  }
}
